import React from "react";
import { Card, Button, Row, Col, Tag, Alert, Spin, List } from "antd";
import {
  CheckCircleOutlined,
  ProfileOutlined,
  InboxOutlined,
  FileTextOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";

import StepHeader from "./StepsHeader";

export default function ReviewConfirmStep({
  basicInfo,
  selectedPackage,
  additionalInfo,
  onBack,
  onCancel,
  onSubmit,
  loading = false,
  projectData,
  userRole,
}) {
  const fieldsBaseOnConfig = projectData?.projectConfig?.metadataFields || {};
  return (
    <div style={{ paddingBottom: 30 }}>
      <StepHeader current={3} />
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(2px)",
          }}
        >
          <Spin size="large" tip="Creating Order..." />
        </div>
      )}
      <Alert
        type="info"
        message="Please review all the information below before confirming the order creation."
        banner
        style={{ marginBottom: 25 }}
      />

      <Card style={{ marginBottom: 20 }}>
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 10 }}
        >
          <ProfileOutlined style={{ marginRight: 8 }} />
          <b>Sample Details</b>
        </div>

        <Row gutter={[30, 10]}>
          {userRole !== "company_admin" && (
            <Col span={12}>
              <p>
                <b>Client</b>
                <br />
                {basicInfo?.client || "-"}
              </p>
            </Col>
          )}
          <Col span={12}>
            <p>
              <b>Project Code</b>
              <br />
              {basicInfo?.projectCode || "-"}
            </p>
          </Col>
          {fieldsBaseOnConfig?.agency?.required === true && (
            <Col span={12}>
              <p>
                <b>Agency</b>
                <br />
                {basicInfo?.agency || "-"}
              </p>
            </Col>
          )}

          {fieldsBaseOnConfig?.commodity?.required === true && (
            <Col span={12}>
              <p>
                <b>Commodity</b>
                <br />
                {basicInfo?.commodity || "-"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.variant?.required === true && (
            <Col span={12}>
              <p>
                <b>Variant</b>
                <br />
                {basicInfo?.variant || "-"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.sampleType?.required === true && (
            <Col span={12}>
              <p>
                <b>Sample Type</b>
                <br />
                {basicInfo?.sampleType || "-"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.sector?.required === true && (
            <Col span={12}>
              <p>
                <b>Sector</b>
                <br />
                {basicInfo?.sector || "-"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.state?.required === true && (
            <Col span={12}>
              <p>
                <b>State</b>
                <br />
                {basicInfo?.state || "-"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.district?.required === true && (
            <Col span={12}>
              <p>
                <b>District</b>
                <br />
                {basicInfo?.district || "-"}
              </p>
            </Col>
          )}

          {fieldsBaseOnConfig?.destinationCountry?.required === true && (
            <Col span={12}>
              <p>
                <b>Destination Country</b>
                <br />
                {basicInfo?.destinationCountry || "-"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.samplingType?.required === true && (
            <Col span={12}>
              <p>
                <b>Sampling Type</b>
                <br />
                {basicInfo?.samplingType || "-"}
              </p>
            </Col>
          )}
          {basicInfo?.samplingType === "Code-wise Sampling" && (
            <Col span={12}>
              <p>
                <b>Codewise Reason</b>
                <br />
                {basicInfo?.codewiseReason || "-"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.expectedConsignmentReadiness?.required ===
            true && (
            <Col span={12}>
              <p>
                <b>Expected Consignment Readiness</b>
                <br />
                {basicInfo?.expectedConsignmentReadiness
                  ? new Date(
                      basicInfo.expectedConsignmentReadiness
                    ).toLocaleDateString("en-GB")
                  : "-"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.expectedShipmentDate?.required === true && (
            <Col span={12}>
              <p>
                <b>Expected Shipment Date</b>
                <br />
                {basicInfo?.expectedShipmentDate
                  ? new Date(basicInfo.expectedShipmentDate).toLocaleDateString(
                      "en-GB"
                    )
                  : "-"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.sampler?.required === true && (
            <Col span={12}>
              <p>
                <b>Sampler</b>
                <br />
                {basicInfo?.sampler || <i>Not assigned</i>}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.siteAddress?.required === true && (
            <Col span={12}>
              <p>
                <b>Site Address</b>
                <br />
                {basicInfo?.siteAddress || "-"}
              </p>
            </Col>
          )}
        </Row>
      </Card>

      <Card style={{ marginBottom: 20 }}>
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 10 }}
        >
          <InboxOutlined style={{ marginRight: 8 }} />
          <b>Testing Package</b>
        </div>

        <p>
          <b>Selected Package</b>
          <br />
          {selectedPackage?.name}
        </p>

        {selectedPackage?.default && (
          <Tag color="geekblue" style={{ marginBottom: 15 }}>
            ⭐ Default Package
          </Tag>
        )}

        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={12}>
            <Card style={{ textAlign: "center", padding: 10 }}>
              <h2>{selectedPackage?.totalParameters}</h2>
              <p>Total Parameters</p>
            </Card>
          </Col>
          <Col span={12}>
            <Card style={{ textAlign: "center", padding: 10 }}>
              <h2>{selectedPackage?.categories?.length || "-"}</h2>
              <p>Categories</p>
            </Card>
          </Col>
        </Row>

        <p>
          <b>Category Breakdown:</b>
        </p>

        {selectedPackage?.categories?.map((cat) => (
          <Tag key={cat.name} style={{ marginBottom: 10 }}>
            {cat.name}: {cat.count}
          </Tag>
        ))}
      </Card>

      <Card style={{ marginBottom: 20 }}>
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 10 }}
        >
          <FileTextOutlined style={{ marginRight: 8 }} />
          <b>Additional Details</b>
        </div>

        <Row gutter={[30, 10]}>
          {fieldsBaseOnConfig?.batchId?.required === true && (
            <Col span={12}>
              <p>
                <b>Batch ID</b>
                <br />
                {additionalInfo?.batchId || "Not provided"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.supplier?.required === true && (
            <Col span={12}>
              <p>
                <b>Supplier</b>
                <br />
                {additionalInfo?.supplier || "Not provided"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.quantity?.required === true && (
            <Col span={12}>
              <p>
                <b>Quantity</b>
                <br />
                {additionalInfo?.quantity || "Not provided"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.quantityUnit?.required === true && (
            <Col span={12}>
              <p>
                <b>Unit</b>
                <br />
                {additionalInfo?.quantityUnit || "Not provided"}
              </p>
            </Col>
          )}
          {fieldsBaseOnConfig?.notes?.required === true && (
            <Col span={24}>
              <p>
                <b>Notes</b>
                <br />
                {additionalInfo?.notes || "No additional notes"}
              </p>
            </Col>
          )}
        </Row>

        {additionalInfo?.documents && additionalInfo.documents.length > 0 && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: 20,
                marginBottom: 10,
              }}
            >
              <PaperClipOutlined style={{ marginRight: 8 }} />
              <b>Uploaded Documents ({additionalInfo.documents.length})</b>
            </div>
            <List
              size="small"
              bordered
              dataSource={additionalInfo.documents}
              renderItem={(file, index) => (
                <List.Item>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <PaperClipOutlined />
                      <span>{file.fileName}</span>
                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      {/* <Tag color="blue">
                        {(file.size / 1024).toFixed(2)} KB
                      </Tag> */}
                      <Tag color="green">
                        {(file.documentType || "Unknown Type").toUpperCase()}
                      </Tag>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </>
        )}
      </Card>

      <Alert
        message={
          <div>
            <b>Ready to Create</b>
            <br />
            Click "Create Order" to confirm and generate your order ID
          </div>
        }
        type="success"
        icon={<CheckCircleOutlined />}
        style={{ marginBottom: 30 }}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          borderTop: "1px solid #eee",
          paddingTop: 20,
        }}
      >
        <Button onClick={onBack}>Back</Button>

        <div style={{ display: "flex", gap: 10 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={onSubmit}
            style={{ padding: "0 28px" }}
          >
            Create Order
          </Button>
        </div>
      </div>
    </div>
  );
}
